import { Box, Button, Typography, TextField, Autocomplete, Tooltip, IconButton } from '@mui/material';
import { ContentCopy, OpenInNew } from '@mui/icons-material';
import * as React from 'react';
import { useCallback, useEffect, useState } from 'react';
import { Vehicle, VEHICLE_FIELDS, Choice } from 'types/vehicles/types';
import { vehiclePageStyles } from '../styles';
import { getShippingLineTrackingLink } from '../../config';
import axiosService from 'utils/axios';
import { Destination } from 'types/entities/destination_type';
import { Importer } from 'types/entities/types';
import { Notify } from 'types/entities/notify_type';
import AddOrEditDestination from 'pages/entities/destinations/AddOrEditDestination';
import AddOrEditImporter from 'pages/entities/importers/AddOrEditImporter';
import AddOrEditNotify from 'pages/entities/notifies/AddOrEditNotify';
import { useCopyToClipboard } from '../hooks/use_copy_to_clipboard';
import '../styles.css';

export interface VehicleShippingSectionData {
  loader: string;
  destination_id: string | null;
  importer_id: string | null;
  notify_id: string | null;
  shipping_line: string | number;
  container_number: string;
  comment: string;
  comment_restricted: string;
}

interface VehicleShippingSectionProps {
  updateSubmitData: (data: VehicleShippingSectionData) => void;
  canView: (fieldName: string) => boolean;
  canEdit: (fieldName: string) => boolean;
  isSuperuser: boolean;
  readOnly?: boolean;
}

export interface VehicleShippingSectionRef {
  updateSectionData: (vehicleData: Vehicle) => void;
  getImporters: () => Importer[];
  getImporterChoice: () => { id: string; name: string } | null;
  refreshImportersList: (onSuccess?: () => void) => void;
}

interface VehicleDropdownOption {
  id: string;
  name: string;
}

const VehicleShippingSection = React.forwardRef(
  ({ updateSubmitData, canView, canEdit, isSuperuser, readOnly }: VehicleShippingSectionProps, ref) => {
    // Shipping line options
    const [shippingLineOptions, setShippingLineOptions] = useState<Choice[]>([]);
    const [isShippingLineOptionsLoading, setIsShippingLineOptionsLoading] = useState(false);

    // Use copy to clipboard hook
    const { copyTooltip, copyToClipboard } = useCopyToClipboard();

    // Shipping states
    const [loaderInput, setLoaderInput] = useState<string>('');
    const [destinationTextInput, setDestinationTextInput] = useState<string>();
    const [destinationChoice, setDestinationChoice] = useState<VehicleDropdownOption | null>(null);
    const [importerChoice, setImporterChoice] = useState<VehicleDropdownOption | null>(null);
    const [notifyChoice, setNotifyChoice] = useState<VehicleDropdownOption | null>(null);
    const [shippingLineChoice, setShippingLineChoice] = useState<Choice | null>(null);
    const [containerNumberInput, setContainerNumberInput] = useState<string>('');
    const [commentInput, setCommentInput] = useState<string>('');
    const [restrictedCommentInput, setRestrictedCommentInput] = useState<string>('');
    // Const [configIDInput, setConfigIDInput] = useState<string>('N/A');

    // Destination options
    const [destinationOptions, setDestinationOptions] = useState<VehicleDropdownOption[]>([]);
    const [isDestinationOptionsLoading, setIsDestinationOptionsLoading] = useState(false);

    // Importer options
    const [importers, setImporters] = useState<Importer[]>([]);

    // Notify options
    const [notifyOptions, setNotifyOptions] = useState<VehicleDropdownOption[]>([]);
    const [isNotifyOptionsLoading, setIsNotifyOptionsLoading] = useState(false);

    // Create Entities states
    const [addNewDestinationModalOpen, setAddNewDestinationModalOpen] = useState(false);
    const [addNewImporterModalOpen, setAddNewImporterModalOpen] = useState(false);
    const [addNewNotifyModalOpen, setAddNewNotifyModalOpen] = useState(false);

    // Helper functions
    const getImporterName = (importer: Importer) => {
      let importerName = '';
      if (importer.person && importer.company) {
        importerName = `${importer.person.first_name} ${importer.person.last_name} - ${importer.company.name}`.trim();
      } else if (importer.person) {
        importerName = `${importer.person.first_name} ${importer.person.last_name}`.trim();
      } else if (importer.company) {
        importerName = importer.company.name;
      }
      return importerName;
    };

    const getNotifyName = (notify: Notify) => {
      return `${notify.company.name} - ${notify.destination.name}`;
    };

    const getShippingLineOptions = useCallback(() => {
      setIsShippingLineOptionsLoading(true);
      axiosService
        .options(`/vehicles/vehicle/`)
        .then(res => {
          setShippingLineOptions(res.data.actions.POST?.shipping_line?.choices || []);
        })
        .finally(() => setIsShippingLineOptionsLoading(false));
    }, []);

    const getDestinationsList = useCallback((onSuccess?: () => void) => {
      setIsDestinationOptionsLoading(true);
      axiosService
        .get(`/entities/destination/`)
        .then(res => {
          const names: VehicleDropdownOption[] = [];
          res.data.forEach((destination: Destination) => names.push({ id: destination.id, name: destination.name }));
          setDestinationOptions(names);
          if (onSuccess) {
            onSuccess();
          }
        })
        .finally(() => setIsDestinationOptionsLoading(false));
    }, []);

    const getImportersList = useCallback((onSuccess?: () => void) => {
      axiosService.get(`/entities/importer/`).then(res => {
        setImporters(res.data);
        if (onSuccess) {
          onSuccess();
        }
      });
    }, []);

    const getNotifiesList = useCallback((onSuccess?: () => void) => {
      setIsNotifyOptionsLoading(true);
      axiosService
        .get(`/entities/notify/`)
        .then(res => {
          const names: VehicleDropdownOption[] = [];
          res.data.forEach((notify: Notify) => {
            names.push({ id: notify.id, name: getNotifyName(notify) });
          });
          setNotifyOptions(names);
          if (onSuccess) {
            onSuccess();
          }
        })
        .finally(() => setIsNotifyOptionsLoading(false));
    }, []);

    useEffect(() => {
      if (readOnly) return;

      getShippingLineOptions();
      getDestinationsList();
      getImportersList();
      getNotifiesList();
    }, [getShippingLineOptions, getDestinationsList, getImportersList, getNotifiesList, readOnly]);

    React.useEffect(() => {
      // When one of the internal states change, update the submit data
      updateSubmitData({
        loader: loaderInput,
        // eslint-disable-next-line camelcase
        destination_id: destinationChoice?.id || null,
        // eslint-disable-next-line camelcase
        importer_id: importerChoice?.id || null,
        // eslint-disable-next-line camelcase
        notify_id: notifyChoice?.id || null,
        // eslint-disable-next-line camelcase
        shipping_line: shippingLineChoice?.value || '',
        // eslint-disable-next-line camelcase
        container_number: containerNumberInput,
        comment: commentInput,
        // eslint-disable-next-line camelcase
        comment_restricted: restrictedCommentInput,
      });
    }, [
      loaderInput,
      destinationChoice,
      importerChoice,
      notifyChoice,
      shippingLineChoice,
      containerNumberInput,
      commentInput,
      restrictedCommentInput,
      updateSubmitData,
    ]);

    React.useImperativeHandle(ref, () => ({
      updateSectionData: (vehicleData: Vehicle) => {
        setLoaderInput(vehicleData?.loader);
        setShippingLineChoice(vehicleData?.shipping_line.value ? vehicleData.shipping_line : null);
        setContainerNumberInput(vehicleData?.container_number);
        setCommentInput(vehicleData?.comment);
        setRestrictedCommentInput(vehicleData?.comment_restricted);
        setDestinationChoice(
          vehicleData?.destination
            ? {
                id: vehicleData.destination.id,
                name: vehicleData.destination.name,
              }
            : null
        );
        setImporterChoice(
          vehicleData?.importer
            ? {
                id: vehicleData.importer.id,
                name: getImporterName(vehicleData.importer),
              }
            : null
        );
        setNotifyChoice(
          vehicleData?.notify
            ? {
                id: vehicleData.notify.id,
                name: getNotifyName(vehicleData.notify),
              }
            : null
        );
      },
      getImporters: () => {
        return importers;
      },
      getImporterChoice: () => {
        return importerChoice;
      },
      refreshImportersList: (onSuccess?: () => void) => {
        getImportersList(onSuccess);
      },
    }));

    return (
      <Box sx={vehiclePageStyles.card}>
        <Box sx={vehiclePageStyles.cardHeader}>Shipping</Box>
        {canView(VEHICLE_FIELDS.LOADER) ? (
          <Box sx={vehiclePageStyles.detailBox}>
            <Typography sx={vehiclePageStyles.detailTitle}>Loading WH</Typography>
            {canEdit(VEHICLE_FIELDS.LOADER) ? (
              <TextField
                variant="standard"
                size="small"
                id="loader"
                value={loaderInput}
                onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                  setLoaderInput(event.target.value);
                }}
                sx={vehiclePageStyles.detailTextField}
                inputProps={{
                  sx: {
                    ...vehiclePageStyles.detailTextFieldInput,
                  },
                }}
              />
            ) : (
              <Box sx={vehiclePageStyles.detailValue}>{loaderInput || '-'}</Box>
            )}
          </Box>
        ) : null}
        {canView(VEHICLE_FIELDS.DESTINATION) ? (
          <Box sx={vehiclePageStyles.detailBox}>
            <Typography sx={vehiclePageStyles.detailTitle}>Destination (POD)</Typography>
            {canEdit(VEHICLE_FIELDS.DESTINATION) ? (
              <Autocomplete
                value={destinationChoice}
                onChange={(event, value) => setDestinationChoice(value)}
                onInputChange={(event, value) => setDestinationTextInput(value)}
                isOptionEqualToValue={(option, value) => option.id === value.id}
                getOptionLabel={option => option.name}
                options={destinationOptions}
                loading={isDestinationOptionsLoading}
                sx={vehiclePageStyles.detailTextField}
                renderInput={params => (
                  <TextField
                    {...params}
                    variant="standard"
                    size="small"
                    InputProps={{
                      ...params.InputProps,
                      sx: {
                        ...vehiclePageStyles.detailTextFieldInput,
                      },
                    }}
                  />
                )}
                noOptionsText={
                  isSuperuser ? (
                    <Button onClick={() => setAddNewDestinationModalOpen(true)}>Add a new Destination</Button>
                  ) : (
                    'No options'
                  )
                }
              />
            ) : (
              <Box sx={vehiclePageStyles.detailValue}>{destinationChoice?.name || '-'}</Box>
            )}
          </Box>
        ) : null}
        {canView(VEHICLE_FIELDS.IMPORTER) ? (
          <Box sx={vehiclePageStyles.detailBox}>
            <Typography sx={vehiclePageStyles.detailTitle}>Importer (Consignee)</Typography>
            {canEdit(VEHICLE_FIELDS.IMPORTER) ? (
              <Autocomplete
                value={importerChoice}
                onChange={(event, value) => setImporterChoice(value)}
                isOptionEqualToValue={(option, value) => option.id === value.id}
                getOptionLabel={option => option.name}
                options={importers.map((importer: Importer) => ({
                  id: importer.id,
                  name: getImporterName(importer),
                }))}
                sx={vehiclePageStyles.detailTextField}
                renderInput={params => (
                  <TextField
                    {...params}
                    variant="standard"
                    size="small"
                    InputProps={{
                      ...params.InputProps,
                      sx: {
                        ...vehiclePageStyles.detailTextFieldInput,
                      },
                    }}
                  />
                )}
                noOptionsText={
                  isSuperuser ? (
                    <Button onClick={() => setAddNewImporterModalOpen(true)}>Add a new Importer</Button>
                  ) : (
                    'No options'
                  )
                }
              />
            ) : (
              <Box sx={vehiclePageStyles.detailValue}>{importerChoice?.name || '-'}</Box>
            )}
          </Box>
        ) : null}
        {addNewImporterModalOpen ? (
          <AddOrEditImporter
            onSave={newImporter => {
              getImportersList(() => {
                setImporterChoice({
                  id: newImporter.id,
                  name: getImporterName(newImporter),
                });
                setAddNewImporterModalOpen(false);
              });
            }}
            modalTitle="Add new importer"
            isModalOpen={addNewImporterModalOpen}
            setModalOpen={setAddNewImporterModalOpen}
          />
        ) : null}
        {canView(VEHICLE_FIELDS.NOTIFY) ? (
          <Box sx={vehiclePageStyles.detailBox}>
            <Typography sx={vehiclePageStyles.detailTitle}>Notify (Customs agent)</Typography>
            {canEdit(VEHICLE_FIELDS.NOTIFY) ? (
              <Autocomplete
                value={notifyChoice}
                onChange={(event, value) => setNotifyChoice(value)}
                isOptionEqualToValue={(option, value) => option.id === value.id}
                getOptionLabel={option => option.name}
                options={notifyOptions}
                loading={isNotifyOptionsLoading}
                sx={vehiclePageStyles.detailTextField}
                renderInput={params => (
                  <TextField
                    {...params}
                    variant="standard"
                    size="small"
                    InputProps={{
                      ...params.InputProps,
                      sx: {
                        ...vehiclePageStyles.detailTextFieldInput,
                      },
                    }}
                  />
                )}
                noOptionsText={
                  isSuperuser ? (
                    <Button onClick={() => setAddNewNotifyModalOpen(true)}>Add a new Notify</Button>
                  ) : (
                    'No options'
                  )
                }
              />
            ) : (
              <Box sx={vehiclePageStyles.detailValue}>{notifyChoice?.name || '-'}</Box>
            )}
          </Box>
        ) : null}
        {addNewNotifyModalOpen ? (
          <AddOrEditNotify
            onSave={newNotify => {
              getNotifiesList(() => {
                setNotifyChoice({
                  id: newNotify.id,
                  name: getNotifyName(newNotify),
                });
                setAddNewNotifyModalOpen(false);
              });
            }}
            modalTitle="Add new notify"
            isModalOpen={addNewNotifyModalOpen}
            setModalOpen={setAddNewNotifyModalOpen}
          />
        ) : null}
        {canView(VEHICLE_FIELDS.SHIPPING_LINE) ? (
          <Box sx={vehiclePageStyles.detailBox}>
            <Typography sx={vehiclePageStyles.detailTitle}>Shipping Line</Typography>
            {canEdit(VEHICLE_FIELDS.SHIPPING_LINE) ? (
              <Autocomplete
                value={shippingLineChoice}
                onChange={(event, value) => setShippingLineChoice(value)}
                isOptionEqualToValue={(option, value) => option.value === value.value}
                getOptionLabel={option => option.display_name}
                options={shippingLineOptions}
                loading={isShippingLineOptionsLoading}
                sx={vehiclePageStyles.detailTextField}
                renderInput={params => (
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: '5px' }}>
                    <TextField
                      {...params}
                      variant="standard"
                      size="small"
                      InputProps={{
                        ...params.InputProps,
                        sx: {
                          ...vehiclePageStyles.detailTextFieldInput,
                        },
                      }}
                    />
                    <Tooltip title="Track">
                      <span>
                        <IconButton
                          size="small"
                          onClick={() =>
                            window.open(getShippingLineTrackingLink(`${shippingLineChoice?.value}`), '_blank')
                          }
                          disabled={!shippingLineChoice?.value}
                        >
                          <OpenInNew fontSize="small" />
                        </IconButton>
                      </span>
                    </Tooltip>
                  </Box>
                )}
              />
            ) : (
              <Box sx={{ ...vehiclePageStyles.detailValue, display: 'flex', alignItems: 'center', gap: '5px' }}>
                {shippingLineChoice?.display_name || '-'}
                <Tooltip title="Track">
                  <span>
                    <IconButton
                      size="small"
                      onClick={() => window.open(getShippingLineTrackingLink(`${shippingLineChoice?.value}`), '_blank')}
                      disabled={!shippingLineChoice?.value}
                    >
                      <OpenInNew fontSize="small" />
                    </IconButton>
                  </span>
                </Tooltip>
              </Box>
            )}
          </Box>
        ) : null}
        {canView(VEHICLE_FIELDS.CONTAINER_NUMBER) ? (
          <Box sx={vehiclePageStyles.detailBox}>
            <Typography sx={vehiclePageStyles.detailTitle}>Container #</Typography>
            {canEdit(VEHICLE_FIELDS.CONTAINER_NUMBER) ? (
              <Box sx={{ ...vehiclePageStyles.detailTextField, display: 'flex', alignItems: 'center', gap: '5px' }}>
                <TextField
                  variant="standard"
                  size="small"
                  id="container-number"
                  disabled={!isSuperuser}
                  value={containerNumberInput}
                  onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                    setContainerNumberInput(event.target.value);
                  }}
                  sx={vehiclePageStyles.detailTextFieldFullWidth}
                  inputProps={{
                    sx: {
                      ...vehiclePageStyles.detailTextFieldInput,
                    },
                  }}
                />
                <Tooltip title={copyTooltip}>
                  <span>
                    <IconButton
                      size="small"
                      onClick={() => copyToClipboard(containerNumberInput)}
                      disabled={!containerNumberInput}
                    >
                      <ContentCopy fontSize="small" />
                    </IconButton>
                  </span>
                </Tooltip>
              </Box>
            ) : (
              <Box sx={{ ...vehiclePageStyles.detailValue, display: 'flex', alignItems: 'center', gap: '5px' }}>
                {containerNumberInput || '-'}
                <Tooltip title={copyTooltip}>
                  <span>
                    <IconButton
                      size="small"
                      onClick={() => copyToClipboard(containerNumberInput)}
                      disabled={!containerNumberInput}
                    >
                      <ContentCopy fontSize="small" />
                    </IconButton>
                  </span>
                </Tooltip>
              </Box>
            )}
          </Box>
        ) : null}
        {/* {isSuperuser ? (
          <Box sx={vehiclePageStyles.detailBox}>
            <Typography sx={vehiclePageStyles.detailTitle}>Config ID</Typography>
            <TextField
              disabled
              variant="standard"
              size="small"
              id="config-id"
              value={configIDInput}
              onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                setConfigIDInput(event.target.value);
              }}
              sx={vehiclePageStyles.detailTextField}
              inputProps={{
                sx: {
                  ...vehiclePageStyles.detailTextFieldInput,
                },
              }}
            />
          </Box>
        ) : null} */}
        <Box sx={vehiclePageStyles.detailsSeparator}></Box>
        {canView(VEHICLE_FIELDS.COMMENT) ? (
          <Box sx={{ ...vehiclePageStyles.detailBox, flexDirection: 'column', alignItems: 'flex-start', gap: '5px' }}>
            <Typography sx={vehiclePageStyles.detailTitle}>Notes</Typography>
            {canEdit(VEHICLE_FIELDS.COMMENT) ? (
              <TextField
                size="small"
                minRows={5}
                multiline
                id="comments"
                value={commentInput}
                onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                  setCommentInput(event.target.value);
                }}
                sx={vehiclePageStyles.detailTextFieldFullWidth}
                inputProps={{
                  sx: {
                    ...vehiclePageStyles.detailTextFieldInput,
                  },
                }}
              />
            ) : (
              <Box sx={vehiclePageStyles.detailValue}>{commentInput || '-'}</Box>
            )}
          </Box>
        ) : null}
        {canView(VEHICLE_FIELDS.PRIVATE_NOTES) ? (
          <Box
            sx={{
              ...vehiclePageStyles.detailBox,
              flexDirection: 'column',
              alignItems: 'flex-start',
              gap: '5px',
              marginTop: '20px',
            }}
          >
            <Typography sx={vehiclePageStyles.detailTitle}>Private Notes</Typography>
            {canEdit(VEHICLE_FIELDS.PRIVATE_NOTES) ? (
              <TextField
                size="small"
                minRows={5}
                multiline
                id="private-comments"
                value={restrictedCommentInput}
                onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                  setRestrictedCommentInput(event.target.value);
                }}
                sx={vehiclePageStyles.detailTextFieldFullWidth}
                inputProps={{
                  sx: {
                    ...vehiclePageStyles.detailTextFieldInput,
                  },
                }}
              />
            ) : (
              <Box sx={vehiclePageStyles.detailValue}>{restrictedCommentInput || '-'}</Box>
            )}
          </Box>
        ) : null}
        {addNewDestinationModalOpen ? (
          <AddOrEditDestination
            destinationName={destinationTextInput}
            onSave={newDestination => {
              getDestinationsList(() => {
                setDestinationChoice({
                  id: newDestination.id,
                  name: newDestination.name,
                });
                setAddNewDestinationModalOpen(false);
              });
            }}
            modalTitle="Add new destination"
            isModalOpen={addNewDestinationModalOpen}
            setModalOpen={setAddNewDestinationModalOpen}
          />
        ) : null}
      </Box>
    );
  }
);

VehicleShippingSection.displayName = 'VehicleShippingSection';

export default VehicleShippingSection;
